/**
 * Stock Hold Timeout configuration (RBA-2149 / RBA-2089)
 * Settings → Inventory → Stock Hold Timeout
 */

import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, take, takeUntil } from 'rxjs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  FormFieldConfig,
  SearchFormComponent,
} from '@app/shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { ConfirmationDialogComponent, ConfirmationButton } from '@app/shared/components/confirmation-dialog/confirmation-dialog.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { PermissionService } from '@app/core/services/permission.service';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';
import {
  CONFIGURABLE_STOCK_HOLD_CATEGORIES,
  compareStockHoldCategoryOrder,
  formatTimeoutMinutes,
  getStockHoldCategoryLabelKey,
} from '@app/shared/utils/stock-hold-timeout.util';
import {
  defaultOnlineStoreStatusSeverity,
  defaultOnlineStoreStatusStyle,
} from '@app/feature/center-business/channel/utils/lazada-integration.util';
import { StockHoldTimeoutFeatureService } from './services/stock-hold-timeout-feature.service';
import { StockHoldTimeoutDialogComponent } from './components/stock-hold-timeout-dialog/stock-hold-timeout-dialog.component';
import {
  STOCK_HOLD_TIMEOUT_ACTION_COLUMN,
  STOCK_HOLD_TIMEOUT_COLUMNS,
  STOCK_HOLD_TIMEOUT_DEFAULT_COLUMNS_ORDER,
  STOCK_HOLD_TIMEOUT_FLEXIBLE_COLUMNS_ORDER,
} from './stock-hold-timeout.config';

export interface StockHoldTimeoutConfigItem {
  id?: number;
  holdCategory: string;
  timeoutMinutes: number;
  enabled: boolean;
  timeoutDisplay?: string;
  categoryLabel?: string;
}

export interface StockHoldTimeoutSearchParams {
  holdCategory?: string;
  enabled?: boolean;
}

@Component({
  selector: 'app-stock-hold-timeout',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    AppButtonComponent,
    AdvancedDataTableComponent,
    SearchFormComponent,
    ToastModule,
    TagModule,
    TooltipModule,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService, DialogService],
  templateUrl: './stock-hold-timeout.component.html',
  styleUrls: ['./stock-hold-timeout.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StockHoldTimeoutComponent implements OnInit, OnDestroy {
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly featureService = inject(StockHoldTimeoutFeatureService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.inventory.stock_hold_timeout.view',
    );
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.inventory.stock_hold_timeout.create',
    );
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.inventory.stock_hold_timeout.edit',
    );
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.inventory.stock_hold_timeout.delete',
    );
  });
  hasAnyActionPermission = computed(
    () => this.canUpdate() || this.canDelete(),
  );

  canAddNew = computed(() => {
    if (!this.canCreate()) {
      return false;
    }
    return CONFIGURABLE_STOCK_HOLD_CATEGORIES.some(
      (category) => !this.categoryCoverage().has(category),
    );
  });

  private dialogRef: DynamicDialogRef | null = null;

  configs = signal<StockHoldTimeoutConfigItem[]>([]);
  categoryCoverage = signal<ReadonlySet<string>>(new Set());
  loading = signal(false);
  totalRecords = signal(0);
  rows = 20;
  page = signal(0);
  searchParams = signal<StockHoldTimeoutSearchParams>({});

  showConfirmationDialog = signal(false);
  confirmationTitle = signal('');
  confirmationMessage = signal('');
  confirmationButtons = signal<ConfirmationButton[]>([]);
  confirmationAction = signal<'delete' | 'statusChange' | null>(null);
  private confirmationData: {
    item?: StockHoldTimeoutConfigItem;
    newEnabled?: boolean;
  } | null = null;
  selectedForDelete: StockHoldTimeoutConfigItem | null = null;

  searchFormConfig = signal<FormFieldConfig[]>(this.buildSearchConfig());

  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      STOCK_HOLD_TIMEOUT_COLUMNS.find((c) => c.prop === prop);
    const cols: TableColumn[] = [];
    for (const key of STOCK_HOLD_TIMEOUT_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(STOCK_HOLD_TIMEOUT_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }
    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      STOCK_HOLD_TIMEOUT_COLUMNS.find((c) => c.prop === prop);
    return STOCK_HOLD_TIMEOUT_FLEXIBLE_COLUMNS_ORDER.map(
      (prop) => fromConfig(prop)!,
    ).filter(Boolean);
  });

  ngOnInit(): void {
    this.loadConfigs();
    this.loadCategoryCoverage();
  }

  ngOnDestroy(): void {
    this.dialogRef?.close();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCategoryLabel(category?: string): string {
    if (!category) return '';
    return this.translateService.instant(getStockHoldCategoryLabelKey(category));
  }

  formatTimeoutDisplay(minutes?: number): string {
    return formatTimeoutMinutes(minutes, this.translateService);
  }

  getStatusDisplayText(enabled: boolean): string {
    return enabled
      ? this.translateService.instant('common.table.status.active')
      : this.translateService.instant('common.table.status.inactive');
  }

  getStatusSeverity(enabled: boolean): string {
    return defaultOnlineStoreStatusSeverity(!!enabled);
  }

  getStatusStyle(enabled: boolean): Record<string, string> {
    return defaultOnlineStoreStatusStyle(!!enabled);
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const buttonElement =
      (target.closest('p-button')?.querySelector('button') as HTMLElement) ||
      (target.closest('button') as HTMLElement) ||
      target;
    this.advancedTable?.openColumnSelector(buttonElement);
  }

  onSearch(searchData: StockHoldTimeoutSearchParams): void {
    this.searchParams.set({ ...searchData });
    this.page.set(0);
    this.loadConfigs();
  }

  onReset(): void {
    this.searchParams.set({});
    this.page.set(0);
    this.loadConfigs();
  }

  onPageChange(event: any): void {
    this.page.set(event.page);
    this.rows = event.rows;
    this.loadConfigs();
  }

  onAdd(): void {
    if (!this.canAddNew()) {
      return;
    }
    this.openDialog(
      this.translateService.instant('stock_hold_timeout.dialog.add_title'),
      { existingCategories: Array.from(this.categoryCoverage()) },
    );
  }

  onEdit(item: StockHoldTimeoutConfigItem): void {
    if (!this.canUpdate()) {
      this.showNoPermissionToast();
      return;
    }
    this.openDialog(
      this.translateService.instant('stock_hold_timeout.dialog.edit_title'),
      { config: item },
    );
  }

  onToggleStatus(item: StockHoldTimeoutConfigItem, newEnabled: boolean): void {
    if (!this.canUpdate()) {
      this.showNoPermissionToast();
      return;
    }

    const category = this.getCategoryLabel(item.holdCategory);
    this.confirmationTitle.set(
      this.translateService.instant(
        newEnabled
          ? 'stock_hold_timeout.dialog.activate_title'
          : 'stock_hold_timeout.dialog.deactivate_title',
      ),
    );
    this.confirmationMessage.set(
      this.translateService.instant(
        newEnabled
          ? 'stock_hold_timeout.messages.confirm_activate'
          : 'stock_hold_timeout.messages.confirm_deactivate',
        { category },
      ),
    );
    this.confirmationButtons.set([
      {
        label: 'common.actions.cancel',
        action: 'cancel',
        outlined: true,
      },
      {
        label: newEnabled
          ? 'stock_hold_timeout.actions.activate'
          : 'stock_hold_timeout.actions.deactivate',
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.confirmationAction.set('statusChange');
    this.confirmationData = { item, newEnabled };
    this.showConfirmationDialog.set(true);
  }

  onDelete(item: StockHoldTimeoutConfigItem): void {
    if (!this.canDelete()) {
      this.showNoPermissionToast();
      return;
    }
    if (item.enabled) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'stock_hold_timeout.messages.cannot_delete_active',
        ),
      });
      return;
    }
    this.selectedForDelete = item;
    this.confirmationTitle.set(
      this.translateService.instant('stock_hold_timeout.dialog.delete_title'),
    );
    this.confirmationMessage.set(
      this.translateService.instant('stock_hold_timeout.messages.confirm_delete', {
        category: this.getCategoryLabel(item.holdCategory),
      }),
    );
    this.confirmationButtons.set([
      {
        label: 'common.actions.cancel',
        action: 'cancel',
        outlined: true,
      },
      {
        label: 'common.actions.delete',
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.confirmationAction.set('delete');
    this.confirmationData = { item };
    this.showConfirmationDialog.set(true);
  }

  onConfirmationDialogAction(action: string): void {
    this.showConfirmationDialog.set(false);

    if (action !== 'confirm') {
      this.confirmationAction.set(null);
      this.confirmationData = null;
      this.selectedForDelete = null;
      return;
    }

    const currentAction = this.confirmationAction();
    if (currentAction === 'statusChange' && this.confirmationData?.item) {
      this.executeStatusChange(
        this.confirmationData.item,
        !!this.confirmationData.newEnabled,
      );
    } else if (currentAction === 'delete' && this.selectedForDelete?.id) {
      if (!this.canDelete()) {
        this.showNoPermissionToast();
      } else {
        this.featureService
          .deleteConfig(this.selectedForDelete.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.page.set(0);
              this.loadConfigs();
              this.loadCategoryCoverage();
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('common.messages.success'),
                detail: this.translateService.instant(
                  'stock_hold_timeout.messages.delete_success',
                ),
              });
            },
            error: (error: unknown) => {
              this.errorDialogService.showError(
                this.errorTranslatorService.translate(
                  extractErrorMessage(error),
                  'stock_hold_timeout.messages.delete_error',
                ),
              );
            },
          });
      }
    }

    this.confirmationAction.set(null);
    this.confirmationData = null;
    this.selectedForDelete = null;
  }

  private executeStatusChange(
    item: StockHoldTimeoutConfigItem,
    newEnabled: boolean,
  ): void {
    if (!this.canUpdate() || !item.id) {
      this.showNoPermissionToast();
      return;
    }

    this.featureService
      .updateConfig({ ...item, enabled: newEnabled })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          const enabledFilter = this.searchParams().enabled;
          if (
            enabledFilter !== undefined &&
            enabledFilter !== updated.enabled
          ) {
            this.configs.update((items) =>
              items.filter((config) => config.id !== updated.id),
            );
            this.totalRecords.update((count) => Math.max(0, count - 1));
          } else {
            this.configs.update((items) =>
              items.map((config) =>
                config.id === updated.id
                  ? this.enrichConfig({ ...config, enabled: updated.enabled })
                  : config,
              ),
            );
          }
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              newEnabled
                ? 'stock_hold_timeout.messages.activate_success'
                : 'stock_hold_timeout.messages.deactivate_success',
            ),
          });
        },
        error: (error: unknown) => {
          this.errorDialogService.showError(
            this.errorTranslatorService.translate(
              extractErrorMessage(error),
              newEnabled
                ? 'stock_hold_timeout.messages.activate_error'
                : 'stock_hold_timeout.messages.deactivate_error',
            ),
          );
        },
      });
  }

  private openDialog(
    header: string,
    data: { config?: StockHoldTimeoutConfigItem; existingCategories?: string[] },
  ): void {
    this.dialogRef = this.dialogService.open(StockHoldTimeoutDialogComponent, {
      header,
      width: '520px',
      modal: true,
      closable: true,
      data,
    });
    this.dialogRef.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) {
          if (!data.config) {
            this.page.set(0);
          }
          this.loadConfigs();
          this.loadCategoryCoverage();
        }
      });
  }

  private loadCategoryCoverage(): void {
    this.featureService
      .pageConfigs(0, CONFIGURABLE_STOCK_HOLD_CATEGORIES.length)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.categoryCoverage.set(
            new Set(result.configs.map((item) => item.holdCategory)),
          );
        },
      });
  }

  private loadConfigs(): void {
    this.loading.set(true);
    const params = this.searchParams();
    this.featureService
      .pageConfigs(
        this.page(),
        this.rows,
        params.holdCategory as any,
        params.enabled,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.configs.set(this.enrichConfigs(result.configs));
          this.totalRecords.set(result.totalRecords);
          this.loading.set(false);
        },
        error: (error: unknown) => {
          this.loading.set(false);
          this.errorDialogService.showError(
            this.errorTranslatorService.translate(
              extractErrorMessage(error),
              'stock_hold_timeout.messages.load_error',
            ),
          );
        },
      });
  }

  private enrichConfig(
    item: StockHoldTimeoutConfigItem,
  ): StockHoldTimeoutConfigItem {
    return {
      ...item,
      categoryLabel: this.getCategoryLabel(item.holdCategory),
      timeoutDisplay: this.formatTimeoutDisplay(item.timeoutMinutes),
    };
  }

  private enrichConfigs(
    configs: StockHoldTimeoutConfigItem[],
  ): StockHoldTimeoutConfigItem[] {
    return [...configs]
      .sort((a, b) =>
        compareStockHoldCategoryOrder(a.holdCategory, b.holdCategory),
      )
      .map((item) => this.enrichConfig(item));
  }

  private buildSearchConfig(): FormFieldConfig[] {
    return [
      {
        key: 'holdCategory',
        label: 'stock_hold_timeout.search.hold_category',
        type: 'select',
        placeholder: 'stock_hold_timeout.search.select_category',
        width: '280px',
        showClear: true,
        options: CONFIGURABLE_STOCK_HOLD_CATEGORIES.map((code) => ({
          label: getStockHoldCategoryLabelKey(code),
          value: code,
        })),
      },
      {
        key: 'enabled',
        label: 'stock_hold_timeout.search.status',
        type: 'select',
        placeholder: 'stock_hold_timeout.search.select_status',
        width: '200px',
        showClear: true,
        options: [
          { label: 'common.table.status.active', value: true },
          { label: 'common.table.status.inactive', value: false },
        ],
      },
    ];
  }

  private showNoPermissionToast(): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant('common.permissions.no_permission'),
      detail: this.translateService.instant('common.permissions.no_permission'),
    });
  }
}
